import React from 'react';
import dynamic from 'next/dynamic';
import { Alert } from '@tmap/mmm-style-guide/src/Alert';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  MoreHoriz, Share, CommentOutlined,
} from '@tmap/mmm-style-guide/src/Icon';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';

import stopEvent from '@tmap/mmm-style-guide/src/util/stopEvent';
import { types } from '@tmap/mmm-core/entityKey';

import 'swiper/css';

import { styled } from '@tmap/mmm-style-guide/src/styled';

import LinkCard from '../linkCard';
import ConditionalLazyLoad from '../conditional-lazy-load';
import FavoriteButton from '../favoriteButton';
import ContributionCardMenu from './contributionCardMenu';
import ContributionMedia from './contributionMedia';
import CardHeader from './cardHeader';
import { CardProfile } from './cardProfile';

const DynamicShareButton = dynamic(() => import('../shareButton'));

const MediaBox = styled(Box)(() => ({
  borderRadius: '8px',
  overflow: 'hidden',
  lineHeight: 0,
  textAlign: 'center',
}));

const HeaderContainer = styled(Grid, {
  shouldForwardProp: (p) => p !== 'useDefaultPadding',
})(({ useDefaultPadding }) => ({
  padding: useDefaultPadding ? 'initial' : '0 !important',
}));

const PostTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const FooterContainer = styled(Grid, {
  shouldForwardProp: (p) => p !== 'squashVerticalPadding',
})(({ theme, squashVerticalPadding }) => ({
  height: '100%',
  paddingTop: squashVerticalPadding ? theme.spacing(1) : theme.spacing(4),
}));

const StyledFavoriteButton = styled(FavoriteButton)(({ theme }) => ({
  '.MuiSvgIcon-root': { fontSize: '16px' },
  color: theme.palette.grey[500],
  minWidth: 0,
  button: { padding: theme.spacing(0.5) },
  '.MuiBox-root': { fontSize: '12px' },
  lineHeight: theme.spacing(1),
}));

const MenuIcon = styled(MoreHoriz)(() => ({
  margin: '-2px',
}));

const StyledShareIcon = styled(Share)(() => ({
  fontSize: '16px',
}));

const StyledCommentIcon = styled(CommentOutlined)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.grey[500],
  fontSize: '16px',
}));

const CommentContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(0.5),
  alignItems: 'center',
  minHeight: theme.spacing(3.5),
}));

function UGCDefaultBody(props) {
  const {
    contribution,
    moderationView = false,
    entityUrl,
    showMenuNearText = false,
    showAuthor = true,
    showDate = true,
    showTopics = true,
    squashVerticalPadding = false,
    showPinned = true,
    allowNonUniformImageHeights = false,
    width,
    previewText,
    previewTextLonger,
    lazyProps = {},
  } = props;

  const {
    media = [],
    author,
    numberOfLikes,
    approved,
  } = contribution;

  return (
    <>
      <Grid container display='block' rowSpacing={2} direction='column'>
        {!approved && !moderationView && (
          <Grid item>
            <Alert severity='warning'>Post is Pending Approval.</Alert>
          </Grid>
        )}
        <HeaderContainer
          item
          useDefaultPadding={showTopics || showPinned || showDate || !showMenuNearText}
        >
          <CardHeader
            contribution={contribution}
            moderationView={moderationView}
            showMenuNearText={showMenuNearText}
            showTopics={showTopics}
            showPinned={showPinned}
          />
        </HeaderContainer>
        {media.length > 0 && (
          <Grid item>
            <ConditionalLazyLoad shouldLazyLoad {...lazyProps}>
              <MediaBox>
                <ContributionMedia
                  media={media[0]}
                  allowNonUniformImageHeights={allowNonUniformImageHeights}
                  width={width}
                />
              </MediaBox>
            </ConditionalLazyLoad>
          </Grid>
        )}
        <Grid item>
          <Grid container>
            <Grid item xs>
              {contribution.title && (
                <PostTitle variant='body2' fontWeight={500}>
                  {contribution.title}
                </PostTitle>
              )}
              <Typography variant='body2'>
                {previewText}{previewTextLonger.length > previewText.length && previewText.length !== 0 && '...'}
              </Typography>
            </Grid>
            {showMenuNearText && !moderationView && (
              <Grid item>
                <ContributionCardMenu contribution={contribution}>
                  {(handleOpenMenu) => (
                    <IconButton size='small' onClick={handleOpenMenu} aria-label='post options menu'>
                      <MenuIcon />
                    </IconButton>
                  )}
                </ContributionCardMenu>
              </Grid>
            )}
          </Grid>
        </Grid>
        {showAuthor && (
          <Grid item>
            <CardProfile
              author={author}
              contribution={contribution}
              showDate={showDate}
              maxBadges={{ desktop: 1 }}
            />
          </Grid>
        )}
      </Grid>
      {approved && !moderationView && (
        <LinkCard.Footer>
          <FooterContainer container justifyContent='space-between' alignItems='flex-end' squashVerticalPadding={squashVerticalPadding}>
            <CommentContainer item>
              <StyledCommentIcon />
              <Typography
                color='textSecondary'
                component='span'
                fontSize='12px'
              >
                {contribution.numberOfComments || ''}
              </Typography>
            </CommentContainer>
            <Grid item onClick={stopEvent}>
              <StyledFavoriteButton
                breakpoint='disabled'
                entity={{ ...contribution, _type: types.CONTRIBUTION }}
                fontSize='small'
              >{numberOfLikes || ''}
              </StyledFavoriteButton>
            </Grid>
            <Grid item onClick={stopEvent}>
              <DynamicShareButton
                ButtonProps={{
                  size: 'small',
                  sx: { color: (theme) => theme.palette.grey[500], minWidth: 0 },
                }}
                url={`${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}${entityUrl}`}
                iconSize='16px'
              ><StyledShareIcon />
              </DynamicShareButton>
            </Grid>
          </FooterContainer>
        </LinkCard.Footer>
      )}
    </>
  );
}

export default UGCDefaultBody;
